import { Layer, Marker, Source } from "@vis.gl/react-maplibre";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { latLngTuplesToLineStringFeature } from "@ryde/shared";

import {
  LASSO_HINT_OFFSET_PX,
  LASSO_LINE_COLOR,
  LASSO_LINE_WIDTH_PX,
  LASSO_MARKER_PASSTHROUGH_STYLE,
  LASSO_MIN_LINE_POINTS,
  LASSO_RUBBER_BAND_DASH,
} from "./maplibre-lasso-select.constants";
import {
  MapLibreLassoHintStyled,
  MapLibreLassoVertexStyled,
} from "./maplibre-lasso-select.styled";
import { resolveLassoHintKey } from "./maplibre-lasso-select.utils";
import { MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT } from "./maplibre-marker.constants";

interface IMapLibreLassoShapeProps {
  /** Corners placed so far, as `[lat, lng]` pairs. */
  points: [number, number][];
  /** Where the pointer is, or null once it has left the map. */
  cursor: [number, number] | null;
}

/**
 * The shape being traced, drawn the way Leaflet-geoman drew it: a solid line through the placed corners,
 * hollow white handles on each, a dashed rubber band running to the cursor, and a hint saying what the next
 * click will do.
 *
 * Purely visual — the gesture itself lives in `useMapLibreLassoSelect`. They are separate so the polygon
 * editor can own the gesture (its toolbar needs to finish or step back a corner from outside the map) while
 * still drawing it exactly as the stations lasso does.
 */
export const MapLibreLassoShape: FC<IMapLibreLassoShapeProps> = ({
  points,
  cursor,
}) => {
  const { t } = useTranslation();

  const placedLine = useMemo(
    () =>
      points.length >= LASSO_MIN_LINE_POINTS
        ? latLngTuplesToLineStringFeature(points)
        : null,
    [points],
  );

  // From the last corner to the cursor, so the next edge is visible before it is committed.
  const rubberBand = useMemo(
    () =>
      cursor && points.length
        ? latLngTuplesToLineStringFeature([points[points.length - 1], cursor])
        : null,
    [points, cursor],
  );

  const hint = t(resolveLassoHintKey(points.length));

  return (
    <>
      {placedLine && (
        <Source id="maplibre-lasso-line" type="geojson" data={placedLine}>
          <Layer
            id="maplibre-lasso-line-layer"
            type="line"
            paint={{
              ["line-color"]: LASSO_LINE_COLOR,
              ["line-width"]: LASSO_LINE_WIDTH_PX,
            }}
          />
        </Source>
      )}

      {rubberBand && (
        <Source id="maplibre-lasso-rubber" type="geojson" data={rubberBand}>
          <Layer
            id="maplibre-lasso-rubber-layer"
            type="line"
            paint={{
              ["line-color"]: LASSO_LINE_COLOR,
              ["line-width"]: LASSO_LINE_WIDTH_PX,
              ["line-dasharray"]: LASSO_RUBBER_BAND_DASH,
            }}
          />
        </Source>
      )}

      {points.map(([lat, lng], index) => (
        <Marker
          key={`lasso-vertex-${index}`}
          longitude={lng}
          latitude={lat}
          anchor="center"
          style={LASSO_MARKER_PASSTHROUGH_STYLE}
          {...MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT}
        >
          <MapLibreLassoVertexStyled />
        </Marker>
      ))}

      {cursor && (
        <Marker
          longitude={cursor[1]}
          latitude={cursor[0]}
          // Centred under the cursor, the way Leaflet placed a "bottom" tooltip — not off to one corner.
          anchor="top"
          offset={[0, LASSO_HINT_OFFSET_PX]}
          style={LASSO_MARKER_PASSTHROUGH_STYLE}
          {...MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT}
        >
          <MapLibreLassoHintStyled>{hint}</MapLibreLassoHintStyled>
        </Marker>
      )}
    </>
  );
};
